import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_ORIGIN } from '../services/api';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para el modal
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_ORIGIN}/api/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRoles(response.data.data || []);
    } catch (err) {
      setError('Error al cargar roles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_ORIGIN}/api/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPermissions(response.data.data || []);
    } catch (err) {
      console.error('Error al cargar permisos:', err);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        permissions: role.permissions?.map(p => p.name) || []
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        permissions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({
      name: '',
      permissions: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      if (editingRole) {
        // Actualizar rol existente
        await axios.put(`${API_ORIGIN}/api/roles/${editingRole.id}`, formData, { headers });
        setSuccess('Rol actualizado exitosamente');
      } else {
        // Crear nuevo rol
        await axios.post(`${API_ORIGIN}/api/roles`, formData, { headers });
        setSuccess('Rol creado exitosamente');
      }

      handleCloseDialog();
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este rol?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_ORIGIN}/api/roles/${roleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Rol eliminado exitosamente');
      fetchRoles();
    } catch (err) {
      setError('Error al eliminar rol: ' + (err.response?.data?.message || err.message));
    }
  };

  const handlePermissionChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      permissions: typeof value === 'string' ? value.split(',') : value,
    });
  };

  if (loading && roles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestión de Roles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Rol
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Estadísticas */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total de Roles
              </Typography>
              <Typography variant="h4">
                {roles.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Permisos Disponibles
              </Typography>
              <Typography variant="h4">
                {permissions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Roles del Sistema
              </Typography>
              <Typography variant="h4">
                {roles.filter(role => ['admin', 'manager', 'user'].includes(role.name)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de Roles */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Permisos</TableCell>
                    <TableCell>Usuarios</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id} hover>
                      <TableCell>{role.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={role.name}
                            color={role.name === 'admin' ? 'error' : role.name === 'manager' ? 'warning' : 'info'}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          {role.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 300 }}>
                          {role.permissions?.slice(0, 3).map((permission) => (
                            <Chip
                              key={permission.id}
                              label={permission.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {role.permissions?.length > 3 && (
                            <Chip
                              label={`+${role.permissions.length - 3} más`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {role.users_count || 0} usuarios
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(role)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          {!['admin', 'manager', 'user'].includes(role.name) && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRole(role.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Modal para Crear/Editar Rol */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nombre del Rol"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              disabled={editingRole && ['admin', 'manager', 'user'].includes(editingRole.name)}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Permisos</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={handlePermissionChange}
                input={<OutlinedInput label="Permisos" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission.id} value={permission.name}>
                    <Checkbox checked={formData.permissions.indexOf(permission.name) > -1} />
                    <ListItemText primary={permission.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingRole ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement; 